import { useFeatureGate } from "@statsig/react-bindings";
import { useCallback } from "react";
import { useLocalStorage } from "usehooks-ts";
import {
  AppConfig,
  DEV_APP_SETTINGS_KEY,
  getDevAppSettings,
  IS_DEV_APP_SETTINGS_ENABLED_KEY,
} from "../config";
import { enableZoomGate } from "../statsig/gates";
import { Statsig } from "../statsig/StatsigProvider";
import { VideoPlatform } from "../video-sdk";
import { VIDEO_PLATFORMS } from "../video-sdk/core/constants";

/**
 * Determines if Zoom video integration is enabled for the current user.
 * This hook implements a provider-dependent feature pattern where patient access
 * to Zoom functionality depends on their provider's Statsig allocation.
 *
 * Uses a feature gate with "providerShortID" (case-sensitive) as the
 * Statsig ID Type, defined as a customID on the StatsigUser.
 * @see gracefullyUpdateStatsigUser for user configuration details
 */
export function useIsZoomEnabled(): [boolean, () => boolean] {
  const { value: isZoomGateEnabled } = useFeatureGate(enableZoomGate);
  const [isDevAppSettingsEnabled] = useLocalStorage<number>(
    IS_DEV_APP_SETTINGS_ENABLED_KEY,
    0,
  );
  const [devAppSettings] = useLocalStorage<Partial<AppConfig>>(
    DEV_APP_SETTINGS_KEY,
    {},
  );
  const hasVideoPlatformOverride =
    isDevAppSettingsEnabled &&
    VIDEO_PLATFORMS.has(devAppSettings?.videoPlatformOverride as VideoPlatform);
  const isZoomEnabled = hasVideoPlatformOverride
    ? devAppSettings?.videoPlatformOverride === VideoPlatform.Zoom
    : isZoomGateEnabled;

  const checkZoomIsEnabled = useCallback(() => {
    const devAppSettings = getDevAppSettings();
    const hasVideoPlatformOverride =
      localStorage.getItem(IS_DEV_APP_SETTINGS_ENABLED_KEY) === "1" &&
      VIDEO_PLATFORMS.has(
        devAppSettings?.videoPlatformOverride as VideoPlatform,
      );

    return hasVideoPlatformOverride
      ? devAppSettings?.videoPlatformOverride === VideoPlatform.Zoom
      : Statsig.checkGate(enableZoomGate);
  }, []);

  return [isZoomEnabled, checkZoomIsEnabled];
}
